import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TableContainer from '@mui/material/TableContainer'

import Pagination from '@/components/ui/pagination/Pagination'
import TextInput from '@/components/ui/inputs/TextInput'

import PatientsTable, { PatientData } from '@/pages/patients/ui/PatientsTable'
import { useNavigate } from 'react-router-dom'

export const patientsPerPageOptions = [5, 10]

export type EmergencyPatientSelectionProps = {
  isSearching?: boolean
  onCreate?: () => void
  onPageChange?: (newPage: number, rowsPerPage: number) => void
  onSearch?: (text: string) => void
  onSelect?: (patientId: string) => void
  patients: PatientData[]
  totalSearchCount: number
}

export function PatientSelection(props: EmergencyPatientSelectionProps) {
  const { isSearching, onCreate, onPageChange, onSearch, onSelect, patients, totalSearchCount } = props
  const { t } = useTranslation()

  const [openDialog, setOpenDialog] = useState<boolean>(false)
  const [page, setPage] = useState<number>(1)
  const [selectedPatientId, setSelectedPatientId] = useState<string>()

  const handleChangePage = (newPage: number) => {
    if (page === newPage) return
    setPage(newPage)
    onPageChange?.(newPage, patientsPerPageOptions[0])
  }

  const handleCreateEmergencyPatient = () => {
    onCreate?.()
  }

  const handleOpenDialog = () => {
    setOpenDialog(true)
    onSearch?.('')
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setSelectedPatientId(undefined)
  }

  const handleConfirm = () => {
    if (!!selectedPatientId) onSelect?.(selectedPatientId)
    handleCloseDialog()
  }

  const handleSearchPatient = (event: React.ChangeEvent<HTMLInputElement>) => {
    onSearch?.(event.target.value)
    setPage(1)
  }

  const handleSelectPatient = (patientData: PatientData) => {
    setSelectedPatientId(patientData._id)
  }

  const navigate = useNavigate()

  return (
    <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
      <Stack alignItems="center" justifyContent="center" spacing={3}>
        <Button color="error" fullWidth onClick={handleCreateEmergencyPatient} variant="contained">
          {t('page.emergency.patientSelection.createEmergencyPatientButton.label')}
        </Button>
        <Button color="primary" fullWidth onClick={handleOpenDialog} variant="contained">
          {t('page.emergency.patientSelection.selectPatientButton.label')}
        </Button>
      </Stack>
      <Dialog onClose={handleCloseDialog} open={openDialog} PaperProps={{ sx: { maxWidth: 'unset', minWidth: '300px', minHeight: '500px' } }}>
        <DialogContent>
          <Stack spacing={2}>
            <TextInput
              id="emergency-search-patient"
              fullWidth
              onChange={handleSearchPatient}
              placeholder={t('page.emergency.patientSelection.searchPatientInput.placeholder')}
            />
            <TableContainer component={Paper}>
              <PatientsTable
                autoHightlight={false}
                loading={isSearching}
                data={patients}
                onSelect={handleSelectPatient}
                onOpen={(patient) => navigate(`/patients/${patient._id}`)}
              />
              <Box sx={{ display: 'flex', justifyContent: 'end', p: '10px' }}>
                <Pagination
                  color="primary"
                  disableChangeRowsPerPage
                  itemsPerPageOptions={patientsPerPageOptions}
                  shape="rounded"
                  page={page}
                  onPageChange={handleChangePage}
                  totalItems={totalSearchCount}
                />
              </Box>
            </TableContainer>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} variant="neutral">
            {t('common:button.close.label')}
          </Button>
          <Button color="primary" disabled={!selectedPatientId} onClick={handleConfirm} variant="contained">
            {t('common:button.confirm.label')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default PatientSelection
